import { Suspense, useState } from 'react';
import { lazyWithRetry } from '@/lib/lazy-with-retry';
import { ChunkErrorBoundary } from '@/common/components/ChunkErrorBoundary';
import { EmailResultCard } from './EmailResultCard';
import { EmailDraftCard, readSent } from './EmailDraftCard';
import { NoteResultCard } from './NoteResultCard';
import { DocResultCard } from './DocResultCard';
import { ContactsGridCard } from './ContactsGridCard';
import { ContactGroupCard } from './ContactGroupCard';
import { ContactDetailCard } from './ContactDetailCard';
import { PlaceResultCard } from './PlaceResultCard';
import { TaskResultCard } from './TaskResultCard';
import { EventsResultCard } from './EventsResultCard';
import { InboxResultCard } from './InboxResultCard';
import { InboxTriageCard } from './InboxTriageCard';
import { TravelResultCard } from './TravelResultCard';
import { ReservationsResultCard } from './ReservationsResultCard';
import { CircleResultCard } from './CircleResultCard';
import { PersonaResultCard } from './PersonaResultCard';
import { SocialResultCard } from './SocialResultCard';

import { BriefingResultCard } from './BriefingResultCard';
import { ReportResultCard } from './ReportResultCard';
import type { ArtifactCardData } from '@/modules/pages/studio/draft';

// Lazy: the studio draft surface pulls the whole studio render stack (recharts
// via common/blocks) — keep it out of the entry chunk; artifact cards are rare
// relative to every other card kind (#5255).
const ArtifactCard = lazyWithRetry(() =>
  import('@/modules/pages/studio/draft').then((m) => ({ default: m.ArtifactCard })),
);
import { BulkDeleteConfirmCard } from './BulkDeleteConfirmCard';
import { calendarFlash } from '@/lib/calendarFlash';
import type {
  BulkDeleteConfirmCardData,
  ContactCardData,
  ContactGroupCardData,
  DocCardData,
  EmailCardData,
  EmailDraftCardData,
  EventsCardData,
  InboxCardData,
  InboxTriageCardData,
  NoteCardData,
  PlaceCardData,
  ReportCardData,
  ResultCard,
  ResultCardKind,
  TaskCardData,
} from './types';
import './result-cards.css';

/**
 * Renders a list of result cards beneath an assistant message. Switches on
 * card.kind. `email` opens a thread; `email_draft` is an editable send card;
 * `note`/`place` open their object; `contact` is a clickable grid; `task` is a
 * self-contained interactive checklist (no onCardAction path — it writes back
 * locally via useUpdateObject).
 */
/** Merge all task cards across the list into a single combined card so that
 *  multiple show_tasks calls (e.g. "oranges and lemons") produce one card. */
function mergeTaskCards(cards: ResultCard[]): ResultCard[] {
  const merged: ResultCard[] = [];
  let taskBucket: TaskCardData['tasks'] = [];
  const seenIds = new Set<string>();
  for (const card of cards) {
    if (card.kind === 'task') {
      const data = card.data as unknown as TaskCardData;
      for (const t of data.tasks ?? []) {
        if (!seenIds.has(t.id)) {
          seenIds.add(t.id);
          taskBucket = taskBucket.concat(t);
        }
      }
    } else {
      if (taskBucket.length > 0) {
        merged.push({ kind: 'task', data: { tasks: taskBucket } as unknown as ResultCard['data'] });
        taskBucket = [];
      }
      merged.push(card);
    }
  }
  if (taskBucket.length > 0) {
    merged.push({ kind: 'task', data: { tasks: taskBucket } as unknown as ResultCard['data'] });
  }
  return merged;
}

/**
 * Derive a stable identity key for an email_draft card so the sent-state
 * registry survives React re-mounts. Uses messageId when available (reply mode),
 * otherwise falls back to a composite of to|subject|threadId.
 */
function deriveDraftCardKey(data: EmailDraftCardData): string {
  if (data.messageId) return `draft:msg:${data.messageId}`;
  const parts = [data.to ?? '', data.subject ?? '', data.threadId ?? ''].join('|');
  return `draft:${parts}`;
}

/**
 * Full sent-state identity key for a draft card: the content-stable rawKey
 * (deriveDraftCardKey) scoped by the parent assistant message id so two draft
 * cards in different messages never share a slot. Single source of truth used by
 * the render path, the durable lazy-init below, AND the durable store in
 * EmailDraftCard — they MUST agree exactly or the localStorage SENT marker the
 * card wrote won't match what the renderer reads back after a refetch (#4982).
 * Exported for tests.
 */
export function draftCardKey(data: EmailDraftCardData, messageId?: string): string {
  const rawKey = deriveDraftCardKey(data);
  return messageId ? `${messageId}:${rawKey}` : rawKey;
}

/** Map a bulk-delete object type to the card 'open' action that surfaces it, so
 *  a previewed item in the confirm card is clickable. For a task, boardId routes
 *  the kanban to the SPECIFIC board and highlights the card. Returns null for
 *  types with no inline open route (those items render as plain text). */
function bulkOpenAction(objectType: string, id: string, boardId?: string): { kind: ResultCardKind; intent: 'open'; data: any } | null {
  switch (objectType) {
    case 'task': return { kind: 'task', intent: 'open', data: { id, boardId } };
    case 'contact': return { kind: 'contact', intent: 'open', data: { id } };
    case 'event': return { kind: 'events', intent: 'open', data: { eventId: id } };
    case 'note': return { kind: 'note', intent: 'open', data: { noteId: id } };
    case 'doc': return { kind: 'doc', intent: 'open', data: { docId: id } };
    case 'place': return { kind: 'place', intent: 'open', data: { placeId: id } };
    default: return null;
  }
}

export function ResultCardRenderer({
  cards,
  onCardAction,
  messageId,
}: {
  cards: ResultCard[];
  onCardAction: (
    a: { kind: ResultCardKind; intent: 'open' | 'send' | 'compose' | 'add'; data: any },
  ) => void | Promise<boolean | { subject?: string; body?: string } | null | void>;
  /** Message id of the parent assistant message — used to scope sent-state registry keys. */
  messageId?: string;
}) {
  // Track which draft cardKeys have been sent in this renderer instance.
  // Complements the module-level sentRegistry in EmailDraftCard for cross-mount
  // survival; this local state drives re-render when onSent fires.
  //
  // Lazy-init from the DURABLE (localStorage) marker for any draft card present
  // at mount so the FIRST paint after a reload/refetch already reflects SENT —
  // not only EmailDraftCard's internal hydration (#4982). Keyed by the same
  // cardKey the renderer passes down, so it matches what the card wrote on send.
  const [sentKeys, setSentKeys] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const card of cards) {
      if (card.kind !== 'email_draft') continue;
      const key = draftCardKey(card.data as unknown as EmailDraftCardData, messageId);
      if (readSent(key)) initial.add(key);
    }
    return initial;
  });

  if (!cards.length) return null;

  const merged = mergeTaskCards(cards);

  return (
    <div className="rc-list" data-testid="result-cards">
      {merged.map((card, i) => {
        switch (card.kind) {
          case 'email':
            return (
              <EmailResultCard
                key={i}
                data={card.data as unknown as EmailCardData}
                onOpen={() => onCardAction({ kind: 'email', intent: 'open', data: card.data })}
              />
            );
          case 'email_draft': {
            const draftData = card.data as unknown as EmailDraftCardData;
            // Scope the CONTENT key by parent messageId when available so two draft
            // cards in different assistant messages don't share the same slot. This
            // drives the in-session registry AND the durable localStorage marker.
            const cardKey = draftCardKey(draftData, messageId);
            // React LIST key must be unique even when two drafts in one message
            // share to+subject (both collapse to the same content key) — so
            // suffix the array index. Distinct from the content-stable cardKey.
            const reactKey = `${cardKey}#${i}`;
            // Reply context the agent attaches to reply cards (and InboxResultCard
            // sets); forward it so Send/Compose thread in-place instead of starting
            // a fresh email. Mirrors InboxResultCard's handlers.
            const replyCtx = {
              ...(draftData.threadId ? { threadId: draftData.threadId } : {}),
              ...(draftData.messageId ? { messageId: draftData.messageId } : {}),
              ...(draftData.accountId ? { accountId: draftData.accountId } : {}),
            };
            return (
              <EmailDraftCard
                key={reactKey}
                cardKey={cardKey}
                sent={sentKeys.has(cardKey)}
                onSent={(k) => setSentKeys((prev) => { const next = new Set(prev); next.add(k); return next; })}
                data={draftData}
                onSend={async (edited) => {
                  // `edited` (the user's final To/subject/body) spreads LAST so a
                  // user-edited recipient can never be clobbered by replyCtx — which
                  // only carries threading ids (threadId/messageId/accountId), not `to`.
                  // Hardens the #4659 override against any future replyCtx.to.
                  const r = await onCardAction({ kind: 'email_draft', intent: 'send', data: { ...replyCtx, ...edited } });
                  return typeof r === 'boolean' ? r : undefined;
                }}
                onCompose={async (args) => {
                  const r = await onCardAction({
                    kind: 'email_draft',
                    intent: 'compose',
                    data: { ...args, contactId: draftData.contactId, actionType: draftData.actionType, ...replyCtx },
                  });
                  return r && typeof r === 'object' ? r : undefined;
                }}
                /* args carries { style, rewriteFrom, avoidBody } */
              />
            );
          }
          case 'note':
            return (
              <NoteResultCard
                key={i}
                data={card.data as unknown as NoteCardData}
                onOpen={() => onCardAction({ kind: 'note', intent: 'open', data: card.data })}
              />
            );
          case 'doc':
            return (
              <DocResultCard
                key={i}
                data={card.data as unknown as DocCardData}
                onOpen={() => onCardAction({ kind: 'doc', intent: 'open', data: card.data })}
              />
            );
          case 'contact': {
            const contactData = card.data as unknown as ContactCardData;
            // A matched cell (has id) opens the contact; an unresolved invitee
            // (no id) routes to the `add` intent (create-from-name/email).
            const activate = (c: { id?: string; name?: string; email?: string }) =>
              c.id
                ? onCardAction({ kind: 'contact', intent: 'open', data: { id: c.id } })
                : onCardAction({ kind: 'contact', intent: 'add', data: { name: c.name, email: c.email } });
            // One match → a rich detail card; several → the compact grid.
            if (contactData.contacts?.length === 1) {
              return (
                <ContactDetailCard
                  key={i}
                  contact={contactData.contacts[0]}
                  onOpen={() => activate(contactData.contacts[0])}
                />
              );
            }
            return (
              <ContactsGridCard
                key={i}
                data={contactData}
                onOpenContact={(id) => onCardAction({ kind: 'contact', intent: 'open', data: { id } })}
                onAddContact={(c) => onCardAction({ kind: 'contact', intent: 'add', data: { name: c.name, email: c.email } })}
              />
            );
          }
          case 'contact_group':
            return (
              <ContactGroupCard
                key={i}
                data={card.data as unknown as ContactGroupCardData}
                onOpenContact={(id) => onCardAction({ kind: 'contact', intent: 'open', data: { id } })}
              />
            );
          case 'place':
            return (
              <PlaceResultCard
                key={i}
                data={card.data as unknown as PlaceCardData}
                onOpen={() => onCardAction({ kind: 'place', intent: 'open', data: card.data })}
              />
            );
          case 'task':
            return (
              <TaskResultCard
                key={i}
                data={card.data as unknown as TaskCardData}
                onOpenBoard={(taskId, boardId) =>
                  onCardAction({ kind: 'task', intent: 'open', data: { id: taskId, boardId } })
                }
              />
            );
          case 'report':
            return (
              <ReportResultCard
                key={i}
                data={card.data as unknown as ReportCardData}
                onOpen={() => onCardAction({ kind: 'report', intent: 'open', data: card.data })}
              />
            );
          case 'artifact':
            // Studio v2 inline DRAFT SURFACE (foundation-spec §6.3, D18). The
            // chips + "Message Studio" input dispatch a chat message via the
            // existing `xavior:send-chat-message` event (the same out-of-band
            // path DailySummaryModal / the creation pipeline use) so the card
            // stays self-contained — no extra ChatBox prop plumbing.
            // TODO(studio-edit-loop): route the edit message to the deck-skill
            // surgical `artifact.*` patch instead of a free-text chat turn.
            // Send is the Wave C terminus — the card hosts its own
            // `ArtifactSendModal` (recipients + cover notes + guarded send), so
            // `onSend` here is telemetry-only (log the open as a card action).
            return (
              <ChunkErrorBoundary key={i} label="Artifact">
                <Suspense fallback={null}>
                  <ArtifactCard
                    data={card.data as unknown as ArtifactCardData}
                    onSendMessage={(message) =>
                      window.dispatchEvent(
                        new CustomEvent('xavior:send-chat-message', { detail: { message } }),
                      )
                    }
                    onSend={(data) => onCardAction({ kind: 'artifact', intent: 'open', data })}
                  />
                </Suspense>
              </ChunkErrorBoundary>
            );
          case 'today':
          case 'briefing': // legacy kind from older persisted messages
            return (
              <BriefingResultCard
                key={i}
                data={card.data as Record<string, unknown>}
                onOpenEvent={(_id, start) => {
                  // Flash the event's day red + jump the calendar to month view
                  // (calendarFlash drives useCalendarNavigation → 'month' + the
                  // red day in MonthView). Use the LOCAL date so it matches the
                  // day cell. Then open the calendar module (no eventId, so we
                  // don't trigger the day-view focus that would override 'month').
                  if (start) {
                    const d = new Date(start);
                    if (!Number.isNaN(d.getTime())) {
                      calendarFlash.request(
                        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
                      );
                    }
                  }
                  onCardAction({ kind: 'events', intent: 'open', data: {} });
                }}
              />
            );
          case 'inbox':
            return (
              <InboxResultCard
                key={i}
                data={card.data as unknown as InboxCardData}
                onCardAction={onCardAction}
              />
            );
          case 'bulk_delete_confirm': {
            // Self-contained: the user clicks Delete here and THIS card performs
            // the deletes via the objects API (the agent never deletes 3+ itself).
            const bd = card.data as unknown as BulkDeleteConfirmCardData;
            const canOpen = bulkOpenAction(bd.objectType, '') !== null;
            return (
              <BulkDeleteConfirmCard
                key={i}
                data={bd}
                onOpenItem={canOpen ? ({ id, boardId }) => {
                  const act = bulkOpenAction(bd.objectType, id, boardId);
                  if (act) onCardAction(act);
                } : undefined}
              />
            );
          }
          case 'travel':
            return <TravelResultCard key={i} />;
          case 'reservations':
            return <ReservationsResultCard key={i} />;
          case 'circle':
            return <CircleResultCard key={i} />;
          case 'persona':
            return <PersonaResultCard key={i} />;
          case 'social':
            return <SocialResultCard key={i} />;

          case 'inbox_triage':
            return (
              <InboxTriageCard
                key={i}
                data={card.data as unknown as InboxTriageCardData}
                onOpenEmail={(row) =>
                  onCardAction({ kind: 'email', intent: 'open', data: { threadId: row.threadId, messageId: row.messageId, integrationId: row.integrationId } })
                }
                onOpenInbox={() => onCardAction({ kind: 'email', intent: 'open', data: {} })}
              />
            );
          case 'events': {
            const eventsData = card.data as unknown as EventsCardData;
            // Track deleted IDs at the renderer level so the onOpenEvent guard
            // has the same set that EventsResultCard determined on mount (#4879).
            // EventsResultCard owns the revalidation; it calls onOpenEvent only
            // for live events, but we guard here defensively in case the
            // component's internal deletedIds set isn't yet settled.
            return (
              <EventsResultCard
                key={i}
                data={eventsData}
                onOpenEvent={(e) => {
                  // Guard: EventsResultCard already blocks deleted-row clicks via
                  // pointer-events:none, but guard onCardAction here too in case
                  // a deleted ID somehow slips through (e.g. rapid re-renders).
                  onCardAction({ kind: 'events', intent: 'open', data: { eventId: e.id, start: e.start } });
                }}
                onOpenRange={() => onCardAction({ kind: 'events', intent: 'open', data: { rangeStart: eventsData.rangeStart, rangeEnd: eventsData.rangeEnd } })}
              />
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}
